import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tickets');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [ticketDetail, setTicketDetail] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [showBanModal, setShowBanModal] = useState(false);
  const [stats, setStats] = useState({ open: 0, inProgress: 0, closed: 0, total: 0 });
  const { user, isLoggedIn } = useAuth();
  const { socket, connected } = useSocket();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  
  // 🆕 REFERENCIAS para autoscroll
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchData();
    if (isAdmin) fetchUsers();
  }, [isLoggedIn, navigate, isAdmin]);

  // 🆕 AUTOSCROLL cuando cambian los mensajes
  useEffect(() => {
    if (ticketDetail?.messages?.length > 0) {
      scrollToBottom();
    }
  }, [ticketDetail?.messages]);

  // 🆕 FUNCIÓN DE SCROLL INTELIGENTE
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Solo hacer scroll si el usuario está cerca del fondo (dentro de 100px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    if (isNearBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 🚀 WebSocket: Escuchar mensajes en tiempo real
  useEffect(() => {
    if (!socket || !selectedTicket) return;
    
    socket.emit('join-ticket', selectedTicket);
    
    socket.on('new-message', (data) => {
      if (data.ticketId === selectedTicket) {
        setTicketDetail(prev => ({
          ...prev,
          messages: [...prev.messages, data.message]
        }));
        // El scroll se hará automáticamente por el useEffect de arriba
      }
    });
    
    return () => {
      socket.emit('leave-ticket', selectedTicket);
      socket.off('new-message');
    };
  }, [socket, selectedTicket]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (isAdmin) {
        const [ticketsRes, statsRes] = await Promise.all([
          fetch('/api/tickets', { headers: { 'Authorization': `Bearer ${token}` }}),
          fetch('/api/tickets/stats/overview', { headers: { 'Authorization': `Bearer ${token}` }})
        ]);
        
        const ticketsData = await ticketsRes.json();
        const statsData = await statsRes.json();
        
        setTickets(ticketsData.tickets || []);
        
        const statsObj = { open: 0, inProgress: 0, closed: 0, total: statsData.total || 0 };
        statsData.stats?.forEach(s => {
          if (s._id === 'open') statsObj.open = s.count;
          if (s._id === 'in-progress') statsObj.inProgress = s.count;
          if (s._id === 'closed') statsObj.closed = s.count;
        });
        setStats(statsObj);
      } else {
        const res = await fetch('/api/tickets/my', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/tickets/admin/users?search=${searchQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTicketDetail(data.ticket);
      setSelectedTicket(ticketId);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const sendMessage = async (ticketId) => {
    if (!newMessage.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/tickets/${ticketId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: newMessage })
      });
      
      if (res.ok) {
        setNewMessage('');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const banUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/tickets/admin/users/${userId}/ban`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          isBanned: true, 
          reason: banReason || 'Violation of terms' 
        })
      });
      
      if (res.ok) {
        setShowBanModal(false);
        setBanReason('');
        fetchUsers();
        alert('User banned successfully');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const unbanUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/tickets/admin/users/${userId}/ban`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isBanned: false })
      });
      fetchUsers();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      fetchData();
      if (ticketDetail) fetchTicketDetail(ticketId);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-500/20 text-yellow-400';
      case 'in-progress': return 'bg-blue-500/20 text-blue-400';
      case 'closed': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  // 🆕 RENDER MESSAGES actualizado con autoscroll
  const renderMessages = (messages) => {
    if (!messages?.length) return <p className="text-text-gray text-sm italic">No messages</p>;
    
    return (
      <div 
        ref={messagesContainerRef}
        className="space-y-3 max-h-96 overflow-y-auto"
      >
        {messages.map((msg, idx) => (
          <div 
            key={idx}
            className={`p-3 rounded-lg ${
              msg.sender === 'admin' ? 'bg-primary/20 ml-8' : 'bg-dark-bg/50 mr-8'
            }`}
          >
            <div className="flex justify-between items-center mb-1">
              <span className={`text-xs font-medium ${
                msg.sender === 'admin' ? 'text-primary' : 'text-text-secondary'
              }`}>
                {msg.sender === 'admin' ? 'Support Team' : 'User'}
              </span>
              <span className="text-xs text-text-gray">
                {new Date(msg.createdAt).toLocaleString()}
              </span>
            </div>
            <p className="text-sm text-white">{msg.message}</p>
          </div>
        ))}
        {/* 🆕 Elemento invisible para scroll */}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  // Vista de USUARIO
  if (!isAdmin) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboard-container py-8">
        <div className="container">
          <h1 className="section-title mb-6">My Support Tickets</h1>
          
          <div className="flex items-center gap-2 mb-4">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-xs text-text-gray">
              {connected ? 'Real-time connection active' : 'Offline mode'}
            </span>
          </div>
          
          <div className="card mb-6">
            <h2 className="text-xl font-semibold mb-4">Create New Ticket</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const ticketData = {
                title: formData.get('title'),
                description: formData.get('description'),
                platform: formData.get('platform')
              };
              
              try {
                const token = localStorage.getItem('token');
                const res = await fetch('/api/tickets', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(ticketData)
                });
                
                const data = await res.json();
                
                if (res.ok) {
                  e.target.reset();
                  fetchData();
                  alert('Ticket created!');
                } else {
                  alert(data.message || 'Error creating ticket');
                }
              } catch (err) {
                alert('Error creating ticket');
              }
            }} className="space-y-4">
              <select name="platform" className="form-input" required>
                <option value="">Select platform...</option>
                <option value="Bluebook">Bluebook</option>
                <option value="Respondus">Respondus</option>
                <option value="ProctorU">ProctorU</option>
                <option value="Honorlock">Honorlock</option>
                <option value="ExamSoft">ExamSoft</option>
                <option value="Other">Other</option>
              </select>
              <input name="title" type="text" className="form-input" placeholder="Title" required />
              <textarea name="description" className="form-input" rows="3" placeholder="Describe your issue..." required></textarea>
              <button type="submit" className="btn btn-primary">Submit Ticket</button>
              <p className="text-xs text-text-gray">Limit: 1 active ticket per user</p>
            </form>
          </div>

          {selectedTicket && ticketDetail ? (
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">{ticketDetail.title}</h2>
                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ticketDetail.status)}`}>
                    {ticketDetail.status}
                  </span>
                </div>
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={() => { setSelectedTicket(null); setTicketDetail(null); }}
                >
                  Back to List
                </button>
              </div>
              
              <p className="text-text-secondary mb-4">{ticketDetail.description}</p>
              
              <div className="border-t border-border-color pt-4 mb-4">
                <h3 className="text-sm font-medium text-text-secondary mb-3">Conversation</h3>
                {renderMessages(ticketDetail.messages)}
              </div>
              
              {ticketDetail.status !== 'closed' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="form-input flex-1"
                    placeholder="Type your reply..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage(ticketDetail._id)}
                  />
                  <button 
                    className="btn btn-primary"
                    onClick={() => sendMessage(ticketDetail._id)}
                  >
                    Send
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold mb-4">My Tickets</h2>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : tickets.length === 0 ? (
                <div className="card text-center py-8">
                  <p className="text-text-secondary">No tickets yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tickets.map(ticket => (
                    <motion.div 
                      key={ticket._id} 
                      className="card cursor-pointer hover:border-primary"
                      onClick={() => fetchTicketDetail(ticket._id)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{ticket.title}</h3>
                          <p className="text-text-secondary text-sm">{ticket.description?.substring(0, 100)}...</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    );
  }

  // Vista de ADMIN
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboard-container py-8">
      <div className="container">
        <h1 className="section-title mb-6">Admin Dashboard</h1>
        
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-xs text-text-gray">
            {connected ? 'Real-time connection active' : 'Offline mode'}
          </span>
        </div>
        
        <div className="flex gap-4 mb-6">
          <button 
            className={`btn ${activeTab === 'tickets' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab('tickets')}
          >
            Tickets ({stats.total})
          </button>
          <button 
            className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab('users')}
          >
            Users ({users.length})
          </button>
        </div>

        {activeTab === 'tickets' && (
          <>
            <div className="stats-grid mb-6">
              <motion.div className="stat-card" whileHover={{ y: -5 }}>
                <h3 className="text-warning">{stats.open}</h3>
                <p>Open</p>
              </motion.div>
              <motion.div className="stat-card" whileHover={{ y: -5 }}>
                <h3 className="text-info">{stats.inProgress}</h3>
                <p>In Progress</p>
              </motion.div>
              <motion.div className="stat-card" whileHover={{ y: -5 }}>
                <h3 className="text-success">{stats.closed}</h3>
                <p>Closed</p>
              </motion.div>
              <motion.div className="stat-card" whileHover={{ y: -5 }}>
                <h3>{stats.total}</h3>
                <p>Total</p>
              </motion.div>
            </div>

            {selectedTicket && ticketDetail ? (
              <div className="card">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-semibold">{ticketDetail.title}</h2>
                    <div className="text-sm text-text-gray">
                      By: {ticketDetail.userName} • {ticketDetail.userEmail}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ticketDetail.status)}`}>
                      {ticketDetail.status}
                    </span>
                    <button 
                      className="btn btn-outline btn-sm"
                      onClick={() => { setSelectedTicket(null); setTicketDetail(null); }}
                    >
                      Back
                    </button>
                  </div>
                </div>
                
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-red-400">User Actions</p>
                      <p className="text-xs text-text-gray">{ticketDetail.userName}</p>
                    </div>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => { setSelectedUser(ticketDetail.user); setShowBanModal(true); }}
                    >
                      Ban User
                    </button>
                  </div>
                </div>
                
                <p className="text-text-secondary mb-4">{ticketDetail.description}</p>
                
                <div className="border-t border-border-color pt-4 mb-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-3">Conversation</h3>
                  {renderMessages(ticketDetail.messages)}
                </div>
                
                {ticketDetail.status !== 'closed' && (
                  <div className="space-y-2">
                    <textarea
                      className="form-input w-full"
                      rows="2"
                      placeholder="Type your response..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <button 
                        className="btn btn-primary"
                        onClick={() => sendMessage(ticketDetail._id)}
                      >
                        Send
                      </button>
                      <button 
                        className="btn btn-success"
                        onClick={() => {
                          sendMessage(ticketDetail._id);
                          updateStatus(ticketDetail._id, 'closed');
                        }}
                      >
                        Send & Close
                      </button>
                      {ticketDetail.status === 'open' && (
                        <button 
                          className="btn btn-info"
                          onClick={() => updateStatus(ticketDetail._id, 'in-progress')}
                        >
                          Mark In Progress
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="card">
                <h2 className="text-2xl font-semibold mb-4">Support Tickets</h2>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-8">No tickets.</div>
                ) : (
                  <div className="space-y-4">
                    {tickets.map(ticket => (
                      <motion.div 
                        key={ticket._id}
                        className="p-4 border border-border-color rounded-lg bg-dark-bg/30 cursor-pointer hover:border-primary"
                        onClick={() => fetchTicketDetail(ticket._id)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-semibold">{ticket.title}</h3>
                            <div className="text-sm text-text-gray">
                              By: {ticket.userName} • {ticket.userEmail} • {ticket.platform}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'users' && (
          <div className="card">
            <h2 className="text-2xl font-semibold mb-4">User Management</h2>
            
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                className="form-input flex-1"
                placeholder="Search by username or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="btn btn-primary" onClick={fetchUsers}>
                Search
              </button>
            </div>
            
            {users.length === 0 ? (
              <div className="text-center py-8">No users found.</div>
            ) : (
              <div className="space-y-3">
                {users.map(u => (
                  <div 
                    key={u._id} 
                    className={`p-4 border rounded-lg flex justify-between items-center ${
                      u.isBanned ? 'border-red-500/50 bg-red-500/10' : 'border-border-color bg-dark-bg/30'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{u.username}</h3>
                        {u.isBanned && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded">
                            BANNED
                          </span>
                        )}
                        {u.role === 'admin' && (
                          <span className="px-2 py-0.5 bg-primary text-white text-xs rounded">
                            ADMIN
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-gray">{u.email}</p>
                      {u.isBanned && u.banReason && (
                        <p className="text-xs text-red-400 mt-1">Reason: {u.banReason}</p>
                      )}
                    </div>
                    
                    {u.role !== 'admin' && (
                      <div>
                        {u.isBanned ? (
                          <button 
                            className="btn btn-success btn-sm"
                            onClick={() => unbanUser(u._id)}
                          >
                            Unban
                          </button>
                        ) : (
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => { setSelectedUser(u); setShowBanModal(true); }}
                          >
                            Ban
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {showBanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4 text-red-400">Ban User</h3>
            <p className="text-text-secondary mb-4">
              Are you sure you want to ban <strong>{selectedUser?.username}</strong>?
            </p>
            <div className="mb-4">
              <label className="block text-sm text-text-secondary mb-2">Reason (optional)</label>
              <textarea
                className="form-input w-full"
                rows="3"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Enter reason for ban..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button 
                className="btn btn-outline"
                onClick={() => { setShowBanModal(false); setBanReason(''); }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => banUser(selectedUser._id)}
              >
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Dashboard;